// My Derwent Hub — client-facing portal (premium fintech-grade redesign, iter 89).
// Accessed via magic-link at /hub/:token. Deep midnight navy + warm cream palette.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Home, FileText, MessageSquare, Target, HelpCircle,
  CheckCircle2, Clock, Upload, Send, ChevronRight, Loader2,
  Building2, Calendar, TrendingUp, User, Phone, Mail, AlertCircle,
  Sparkles, Info, Bell, Search, X, KeyRound, Compass, ArrowRight,
  Zap, PhoneCall, Wallet, MapPin, Trophy, PartyPopper,
  Percent, Gem, RefreshCw, CalendarClock, Calculator, PiggyBank, LineChart,
  Share2, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Brand tokens ───────────────────────────────────────────────────
const HUB_NAVY = '#15133A';
const HUB_NAVY_LIGHT = '#1E1B52';
const HUB_NAVY_DARK = '#0F0D2A';
const HUB_CREAM = '#F5F1EA';
const HUB_CREAM_HOVER = '#EAE5D9';

const HUB_FONT_HEAD = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const HUB_FONT_BODY = "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─── Helpers ────────────────────────────────────────────────────────
const STAGE_ICONS = {
  enquiry_received:        Sparkles,
  strategy_session_booked: Calendar,
  documents_requested:     FileText,
  documents_received:      CheckCircle2,
  pre_assessment:          TrendingUp,
  options_being_prepared:  Sparkles,
  application_ready:       CheckCircle2,
  submitted_to_lender:     Send,
  credit_assessment:       Clock,
  conditional_approval:    CheckCircle2,
  valuation_ordered:       Home,
  formal_approval:         CheckCircle2,
  loan_documents_issued:   FileText,
  loan_documents_signed:   CheckCircle2,
  settlement_booked:       Calendar,
  settled:                 KeyRound,
  post_settlement_review:  TrendingUp,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtCurrency = (n) => {
  if (n == null || n === '') return '—';
  const num = typeof n === 'number' ? n : parseFloat(String(n).replace(/[^\d.-]/g, ''));
  if (!isFinite(num)) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(num);
};

const greetingByHour = (name) => {
  const h = new Date().getHours();
  const salut = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return `${salut}${name ? `, ${name}` : ''}`;
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const d1 = new Date(a);
  const d2 = new Date(b);
  return Math.round((d1 - d2) / 86400000);
};

// Roll-up count animation for hero numbers
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const t = typeof target === 'number' ? target : parseFloat(String(target).replace(/[^\d.-]/g, ''));
    if (!isFinite(t)) { setVal(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(t * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

// ─── Root ───────────────────────────────────────────────────────────
export default function MyDerwentHub() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('home');
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/hub/public/${token}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load your hub');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Progress-celebration trigger — fires once when the stage advances
  // beyond what the local browser last saw.
  useEffect(() => {
    if (!data?.client) return;
    const stage = data.client.stage;
    if (!stage) return;
    const key = `hub-last-stage-${token}`;
    const last = window.localStorage.getItem(key);
    if (last && last !== stage) {
      setShowCelebration(true);
      window.setTimeout(() => setShowCelebration(false), 4500);
    }
    window.localStorage.setItem(key, stage);
  }, [data?.client?.stage, token, data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" style={{ backgroundColor: HUB_CREAM, fontFamily: HUB_FONT_BODY }}>
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your hub…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: HUB_CREAM, fontFamily: HUB_FONT_BODY }}>
        <div className="max-w-sm text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
          <p className="text-sm text-slate-700">{error}</p>
        </div>
      </div>
    );
  }

  const client = data.client;
  const updates = data.updates || [];
  const docs = data.documents || [];
  const requests = data.requests || [];
  const stages = data.stages || [];
  const stageLabels = data.stage_labels || {};
  const faqs = data.faqs || [];
  const howtos = data.howtos || [];
  const pendingDocs = docs.filter((d) => d.status === 'requested' || d.status === 'needs_correction').length;
  const newRequests = requests.filter((r) => r.status === 'new').length;
  const unreadCount = pendingDocs + newRequests;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: HUB_CREAM, fontFamily: HUB_FONT_BODY, color: HUB_NAVY }}
    >
      <div className="lg:flex lg:min-h-screen">
        <DesktopSidebar tab={tab} onTab={setTab} client={client} docsBadge={pendingDocs} />

        <div className="flex-1 min-w-0 pb-28 lg:pb-8">
          <TopHeader client={client} unreadCount={unreadCount} onGoTab={setTab} />

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-6">
            {tab === 'home' && (
              <HomeTab
                client={client} updates={updates} docs={docs} requests={requests}
                stages={stages} stageLabels={stageLabels} onGoTab={setTab}
                token={token} refresh={load}
              />
            )}
            {tab === 'loan' && <LoanTab loan={client.loan || {}} client={client} />}
            {tab === 'docs' && <DocsTab docs={docs} token={token} refresh={load} />}
            {tab === 'goals' && <GoalsTab goals={client.goals || {}} />}
            {tab === 'help' && <HelpTab requestTypes={data.request_types} faqs={faqs} howtos={howtos} token={token} refresh={load} />}
            {tab === 'profile' && <ProfileTab client={client} token={token} refresh={load} />}
          </div>
        </div>
      </div>

      <BottomNav tab={tab} onTab={setTab} unreadDocs={pendingDocs} />

      {tab !== 'help' && (
        <button
          type="button"
          onClick={() => setTab('help')}
          data-testid="hub-fab-request"
          className="lg:hidden fixed bottom-24 right-4 z-20 w-14 h-14 rounded-full text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: HUB_NAVY }}
          aria-label="Send a request"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {showCelebration && <StageCelebration stageLabel={client.stage_label} />}
    </div>
  );
}

// ─── Celebration overlay ────────────────────────────────────────────
const StageCelebration = ({ stageLabel }) => (
  <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center" data-testid="hub-celebration">
    <div className="animate-in fade-in zoom-in-95 duration-500 rounded-3xl shadow-2xl px-6 py-5 flex items-center gap-4 pointer-events-auto"
         style={{ backgroundColor: HUB_CREAM, border: `1px solid ${HUB_NAVY}20` }}>
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: HUB_NAVY }}>
        <PartyPopper className="w-6 h-6" style={{ color: HUB_CREAM }} />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Milestone reached</p>
        <p className="text-lg font-medium mt-0.5" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{stageLabel || 'Your application progressed'}</p>
      </div>
    </div>
  </div>
);

// ─── Desktop sidebar (dark navy, premium) ──────────────────────────
const DesktopSidebar = ({ tab, onTab, client, docsBadge = 0 }) => {
  const items = [
    { key: 'home', label: 'Dashboard', icon: Home },
    { key: 'loan', label: 'Your home loan', icon: Building2 },
    { key: 'docs', label: 'Documents', icon: FileText, badge: docsBadge },
    { key: 'goals', label: 'Your goals', icon: Target },
    { key: 'help', label: 'Help & requests', icon: HelpCircle },
    { key: 'profile', label: 'Your profile', icon: User },
  ];
  return (
    <aside
      className="hidden lg:flex flex-col w-[280px] sticky top-0 h-screen"
      style={{
        backgroundColor: HUB_NAVY,
        color: HUB_CREAM,
        backgroundImage: `radial-gradient(1200px 400px at 100% 0%, ${HUB_NAVY_LIGHT} 0%, transparent 60%)`,
      }}
    >
      <div className="px-6 py-6 border-b" style={{ borderColor: `${HUB_CREAM}20` }}>
        <div className="rounded-2xl p-3 flex items-center justify-center" style={{ backgroundColor: HUB_CREAM }}>
          <img
            src="/my-derwent-hub-logo.png"
            alt="My Derwent Hub"
            data-testid="hub-sidebar-logo"
            className="h-12 w-auto select-none"
          />
        </div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] mt-4 opacity-70">Derwent Finance</p>
        <p className="text-base font-medium leading-tight mt-1" style={{ fontFamily: HUB_FONT_HEAD }}>
          {client.first_name} {client.last_name}
        </p>
        {client.reference_number && (
          <p className="text-[10px] font-mono mt-1 opacity-50">Ref · {client.reference_number}</p>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map(({ key, label, icon: Icon, badge }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTab(key)}
              data-testid={`hub-nav-${key}`}
              className="w-full flex items-center gap-3 rounded-xl text-sm px-3.5 py-3 transition-all duration-200"
              style={{
                backgroundColor: active ? HUB_CREAM : 'transparent',
                color: active ? HUB_NAVY : HUB_CREAM,
                fontWeight: active ? 600 : 500,
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = `${HUB_CREAM}12`; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Icon className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {badge > 0 && (
                <span
                  className="min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: active ? HUB_NAVY : '#F59E0B', color: active ? HUB_CREAM : '#fff' }}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t" style={{ borderColor: `${HUB_CREAM}20` }}>
        <div className="rounded-2xl p-3.5" style={{ backgroundColor: `${HUB_CREAM}0F`, border: `1px solid ${HUB_CREAM}20` }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm relative"
                 style={{ backgroundColor: HUB_CREAM, color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>
              {(client.broker_name || '?').slice(0, 1)}
              {/* Broker on-the-case pulse */}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2" style={{ backgroundColor: '#22C55E', ringColor: HUB_NAVY }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] uppercase tracking-[0.2em] opacity-60">Your broker</p>
              <p className="text-sm font-medium truncate" style={{ fontFamily: HUB_FONT_HEAD }}>{client.broker_name || '—'}</p>
            </div>
          </div>
          <p className="text-[10px] mt-2 flex items-center gap-1.5" style={{ color: '#86EFAC' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#22C55E' }} />
            Working on your file today
          </p>
        </div>
      </div>
    </aside>
  );
};

// ─── Top header (mobile + desktop right-side) ──────────────────────
const TopHeader = ({ client, unreadCount, onGoTab }) => {
  const [notifOpen, setNotifOpen] = useState(false);
  return (
    <header
      className="sticky top-0 z-30"
      style={{ backgroundColor: `${HUB_CREAM}E6`, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${HUB_NAVY}10` }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0 lg:hidden flex items-center gap-2.5">
          <img
            src="/my-derwent-hub-logo.png"
            alt="My Derwent Hub"
            data-testid="hub-header-logo"
            className="h-8 w-auto select-none flex-shrink-0"
          />
        </div>
        <div className="hidden lg:block min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>
            My Derwent Hub
          </p>
          <p className="text-lg font-medium leading-tight truncate" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>
            {greetingByHour(client.first_name)}
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onGoTab('help')}
            data-testid="hub-header-help"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ color: HUB_NAVY }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${HUB_NAVY}0F`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Help & FAQs"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => setNotifOpen(!notifOpen)}
            data-testid="hub-header-bell"
            className="relative w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ color: HUB_NAVY }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${HUB_NAVY}0F`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                    style={{ backgroundColor: '#F59E0B' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {notifOpen && (
        <div className="absolute right-4 sm:right-6 lg:right-10 top-16 z-20 w-80 rounded-2xl shadow-2xl overflow-hidden"
             style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}15` }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${HUB_NAVY}10` }}>
            <p className="text-sm font-medium" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>Notifications</p>
            <button type="button" onClick={() => setNotifOpen(false)} className="w-6 h-6 rounded flex items-center justify-center" style={{ color: HUB_NAVY }}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto p-3 space-y-2">
            {unreadCount === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: HUB_NAVY, opacity: 0.55 }}>You&apos;re all caught up.</p>
            ) : (
              <button
                type="button"
                onClick={() => { onGoTab('docs'); setNotifOpen(false); }}
                className="w-full text-left rounded-lg p-2.5 transition-colors"
                style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>Docs waiting on you</p>
                <p className="text-xs mt-0.5" style={{ color: HUB_NAVY }}>Your broker needs a couple of things to move forward.</p>
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

// ─── Bottom nav (mobile) ───────────────────────────────────────────
const BottomNav = ({ tab, onTab, unreadDocs = 0 }) => {
  const tabs = [
    { key: 'home', label: 'Home', icon: Home },
    { key: 'loan', label: 'Loan', icon: Building2 },
    { key: 'docs', label: 'Docs', icon: FileText, badge: unreadDocs },
    { key: 'goals', label: 'Goals', icon: Target },
    { key: 'help', label: 'Help', icon: HelpCircle },
    { key: 'profile', label: 'Profile', icon: User },
  ];
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-20"
         style={{ backgroundColor: '#fff', borderTop: `1px solid ${HUB_NAVY}10`, boxShadow: '0 -4px 20px -4px rgba(21,19,58,0.08)' }}>
      <div className="max-w-4xl mx-auto grid grid-cols-6">
        {tabs.map(({ key, label, icon: Icon, badge }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTab(key)}
              data-testid={`hub-tab-${key}`}
              className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold uppercase tracking-wide transition-colors relative"
              style={{ color: active ? HUB_NAVY : `${HUB_NAVY}80` }}
            >
              <Icon className="w-5 h-5" style={{ color: active ? HUB_NAVY : `${HUB_NAVY}80` }} />
              {label}
              {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full" style={{ backgroundColor: HUB_NAVY }} />}
              {badge > 0 && (
                <span className="absolute top-1.5 right-2 sm:right-4 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                      style={{ backgroundColor: '#F59E0B' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// HOME tab
// ═══════════════════════════════════════════════════════════════════
const HomeTab = ({ client, updates, docs, requests, stages, stageLabels, onGoTab, token, refresh }) => {
  const pendingDocs = docs.filter((d) => d.status === 'requested' || d.status === 'needs_correction').length;
  const settlementDays = client.loan?.settlement_date ? daysBetween(client.loan.settlement_date, new Date().toISOString()) : null;
  const progress = client.progress || { index: 0, total: (stages && stages.length) || 17, percent: 0 };
  const milestone = progress.percent >= 100 ? 100
                  : progress.percent >= 75 ? 75
                  : progress.percent >= 50 ? 50
                  : progress.percent >= 25 ? 25
                  : null;

  const nextStages = useMemo(() => {
    const currentIdx = stages.indexOf(client.stage);
    if (currentIdx < 0) return [];
    return stages.slice(currentIdx + 1, currentIdx + 4).map((s) => ({ key: s, label: stageLabels[s] || s }));
  }, [stages, client.stage, stageLabels]);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <ConciergeBanner client={client} />
      <GreetingHero client={client} onGoTab={onGoTab} />
      <QuickActionsRow onGoTab={onGoTab} />
      <HeroApplicationCard client={client} progress={progress} nextStages={nextStages} onGoTab={onGoTab} />
      {milestone && <MilestoneBanner milestone={milestone} client={client} />}
      {settlementDays !== null && settlementDays >= 0 && settlementDays <= 120 && (
        <SettlementCountdown days={settlementDays} settlementDate={client.loan.settlement_date} />
      )}

      {/* Post-settlement retention widgets — only after settlement */}
      {client.anniversary_review?.review_due && (
        <AnniversaryBanner review={client.anniversary_review} token={token} refresh={refresh} client={client} />
      )}
      {client.property_valuation && (
        <PropertyValueCard valuation={client.property_valuation} />
      )}
      {client.equity && (
        <EquityCard equity={client.equity} token={token} refresh={refresh} />
      )}
      {client.rate_watch && (
        <RateWatchCard rate={client.rate_watch} token={token} refresh={refresh} />
      )}

      <BentoGrid client={client} pendingDocs={pendingDocs} docs={docs} onGoTab={onGoTab} />

      {client.next_action_for_customer && (
        <ActionCard action={client.next_action_for_customer} onGoTab={onGoTab} />
      )}

      {client.id_verification && <IdVerifyCard idv={client.id_verification} token={token} />}

      <ExpectedUpdatesCard client={client} />
      <StageTimeline currentStage={client.stage} stages={stages} stageLabels={stageLabels}
                     stageUpdatedAt={client.stage_updated_at} stageNotes={client.stage_notes} />
      <ActivityFeed updates={updates} />
      <BrokerCard client={client} />
      <ComposeMessage token={token} refresh={refresh} />
    </div>
  );
};

// ─── Concierge banner (personalised broker note) ───────────────────
const ConciergeBanner = ({ client }) => {
  const broker = client.broker_name || 'Your broker';
  const stageLine = client.stage_notes || client.stage_happening;
  const eta = client.expected_broker_update ? fmtDate(client.expected_broker_update) : null;
  const line = stageLine
    ? `${broker.split(' ')[0]} is ${stageLine.charAt(0).toLowerCase() + stageLine.slice(1)}${eta ? ` — expect the next update by ${eta}.` : '.'}`
    : `${broker.split(' ')[0]} is on your file. We'll keep you posted here every step of the way.`;
  return (
    <div
      data-testid="hub-concierge-banner"
      className="rounded-2xl p-4 sm:p-5 flex items-start gap-3"
      style={{
        backgroundColor: `${HUB_NAVY}0A`,
        border: `1px solid ${HUB_NAVY}18`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: HUB_NAVY }}>
        <MessageSquare className="w-4 h-4" style={{ color: HUB_CREAM }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Concierge update</p>
        <p className="text-sm sm:text-[15px] leading-relaxed mt-0.5" style={{ color: HUB_NAVY }}>{line}</p>
      </div>
    </div>
  );
};

// ─── Greeting + Hero Loan Number ───────────────────────────────────
const GreetingHero = ({ client, onGoTab }) => {
  const loanAmount = client.loan?.amount;
  const displayed = useCountUp(loanAmount || 0);
  const hasAmount = !!loanAmount && loanAmount > 0;
  return (
    <div data-testid="hub-greeting-hero">
      <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.28em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>
        {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
      <h1
        className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight"
        style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.02em' }}
      >
        {greetingByHour(client.first_name)}
      </h1>
      {hasAmount && (
        <div className="mt-4 rounded-3xl p-6 sm:p-7 relative overflow-hidden"
             style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}10`, boxShadow: '0 1px 3px rgba(21,19,58,0.04)' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Your home loan</p>
              <p
                data-testid="hub-hero-loan-amount"
                className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter"
                style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }}
              >
                {fmtCurrency(Math.round(displayed))}
              </p>
              {client.loan?.property_address && (
                <p className="mt-3 text-sm flex items-center gap-1.5" style={{ color: HUB_NAVY, opacity: 0.65 }}>
                  <MapPin className="w-3.5 h-3.5" /> {client.loan.property_address}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onGoTab('loan')}
              data-testid="hub-hero-view-loan"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-4 py-2 transition-all duration-200 hover:scale-105 flex-shrink-0"
              style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
            >
              View details <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Quick Actions ──────────────────────────────────────────────────
const QuickActionsRow = ({ onGoTab }) => {
  const actions = [
    { key: 'docs',    icon: Upload,    label: 'Upload docs',   onClick: () => onGoTab('docs') },
    { key: 'message', icon: MessageSquare, label: 'Message broker', onClick: () => onGoTab('home') /* scroll target could be added */ },
    { key: 'help',    icon: PhoneCall, label: 'Get help',      onClick: () => onGoTab('help') },
    { key: 'profile', icon: User,      label: 'Update details', onClick: () => onGoTab('profile') },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3" data-testid="hub-quick-actions">
      {actions.map(({ key, icon: Icon, label, onClick }) => (
        <button
          key={key}
          type="button"
          onClick={onClick}
          data-testid={`hub-quick-${key}`}
          className="rounded-2xl px-4 py-3 flex items-center gap-2.5 transition-all duration-200 hover:-translate-y-0.5 group"
          style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}12`, color: HUB_NAVY }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
               style={{ backgroundColor: `${HUB_NAVY}0A` }}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-xs sm:text-sm font-semibold text-left">{label}</span>
        </button>
      ))}
    </div>
  );
};

// ─── HERO application card (navy + cream, journey preview) ─────────
const HeroApplicationCard = ({ client, progress, nextStages, onGoTab }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setWidth(Math.max(4, progress.percent)), 60);
    return () => window.clearTimeout(t);
  }, [progress.percent]);

  return (
    <div
      className="rounded-3xl p-6 sm:p-7 lg:p-8 relative overflow-hidden shadow-lg"
      style={{
        backgroundImage: `linear-gradient(135deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 50%, ${HUB_NAVY_DARK} 100%)`,
        color: HUB_CREAM,
      }}
      data-testid="hub-hero-application"
    >
      {/* soft dot texture */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, ${HUB_CREAM} 1px, transparent 0)`,
        backgroundSize: '22px 22px',
      }} />
      {/* corner glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none opacity-30" style={{ backgroundColor: HUB_NAVY_LIGHT, filter: 'blur(60px)' }} />

      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: HUB_CREAM, opacity: 0.6 }}>Where you&apos;re at</p>
            <p
              className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-tight"
              style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.02em' }}
              data-testid="hub-hero-stage"
            >
              {client.stage_label || 'Getting started'}
            </p>
          </div>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.18em] rounded-full px-3 py-1.5 whitespace-nowrap"
            style={{ backgroundColor: `${HUB_CREAM}12`, border: `1px solid ${HUB_CREAM}30`, color: HUB_CREAM }}
          >
            Step {progress.index + 1} of {progress.total}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${HUB_CREAM}15` }}>
            <div
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${width}%`, backgroundColor: HUB_CREAM }}
              data-testid="hub-hero-progress-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.22em]" style={{ color: HUB_CREAM, opacity: 0.55 }}>
            <span>{progress.percent}% of the journey</span>
            {client.stage_updated_at && <span>Updated {fmtDate(client.stage_updated_at)}</span>}
          </div>
        </div>

        {/* Journey preview chips */}
        {nextStages.length > 0 && (
          <div className="mt-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] mb-2.5" style={{ color: HUB_CREAM, opacity: 0.55 }}>What&apos;s coming up</p>
            <div className="flex items-center gap-2 flex-wrap">
              {nextStages.map((s, i) => (
                <React.Fragment key={s.key}>
                  <span
                    data-testid={`hub-next-stage-${i}`}
                    className="text-xs font-medium rounded-full px-3 py-1.5 whitespace-nowrap"
                    style={{ backgroundColor: `${HUB_CREAM}0F`, border: `1px solid ${HUB_CREAM}25`, color: HUB_CREAM }}
                  >
                    {s.label}
                  </span>
                  {i < nextStages.length - 1 && <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: HUB_CREAM, opacity: 0.4 }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Action strip */}
        {client.next_action_for_customer && (
          <div
            className="mt-6 rounded-2xl p-4 flex items-start gap-3"
            style={{ backgroundColor: '#FBBF2422', border: '1px solid #F59E0B60' }}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F59E0B' }}>
              <Zap className="w-4 h-4" style={{ color: HUB_NAVY }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-0.5" style={{ color: '#FBBF24' }}>What you need to do</p>
              <p className="text-sm font-medium" style={{ color: HUB_CREAM }}>{client.next_action_for_customer}</p>
            </div>
            <button
              type="button"
              onClick={() => onGoTab('docs')}
              className="rounded-full text-[11px] font-semibold px-3.5 py-2 flex-shrink-0 transition-transform hover:scale-105"
              style={{ backgroundColor: HUB_CREAM, color: HUB_NAVY }}
            >
              Take action
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Motivational milestone banner ─────────────────────────────────
const MilestoneBanner = ({ milestone, client }) => {
  const copy = {
    25:  { title: 'A quarter of the way through', body: 'Great start! Your file is moving nicely.' },
    50:  { title: 'Halfway there', body: 'You\'re past the halfway mark — we\'ll keep the momentum going.' },
    75:  { title: 'Three-quarters done', body: 'Nearly there. The finish line is in sight.' },
    100: { title: 'You made it', body: 'Settlement day is here — congratulations from all of us at Derwent.' },
  }[milestone];
  return (
    <div
      data-testid={`hub-milestone-${milestone}`}
      className="rounded-3xl p-5 sm:p-6 flex items-center gap-4 relative overflow-hidden"
      style={{ backgroundColor: HUB_CREAM_HOVER, border: `1px solid ${HUB_NAVY}18` }}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-40 pointer-events-none" style={{ backgroundColor: `${HUB_NAVY}12` }} />
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: HUB_NAVY }}>
        <Trophy className="w-6 h-6" style={{ color: HUB_CREAM }} />
      </div>
      <div className="min-w-0 relative">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Milestone · {milestone}%</p>
        <p className="text-lg sm:text-xl font-medium mt-0.5" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{copy.title}</p>
        <p className="text-sm mt-1" style={{ color: HUB_NAVY, opacity: 0.7 }}>{copy.body}</p>
      </div>
    </div>
  );
};

// ─── Settlement countdown with shimmer ─────────────────────────────
const SettlementCountdown = ({ days, settlementDate }) => {
  const shownDays = useCountUp(days, 800);
  const isKeys = days === 0;
  return (
    <div
      data-testid="hub-settlement-countdown"
      className="rounded-3xl p-6 sm:p-7 relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(120deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`,
        color: HUB_CREAM,
      }}
    >
      {/* shimmer sweep */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute inset-y-0 w-1/3"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${HUB_CREAM}18 50%, transparent 100%)`,
            animation: 'hub-shimmer 3.5s linear infinite',
          }}
        />
      </div>
      <style>{`@keyframes hub-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>

      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Estimated settlement</p>
          <div className="mt-2 flex items-baseline gap-3">
            <p
              className="text-6xl sm:text-7xl font-light tracking-tighter"
              style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.04em' }}
            >
              {Math.round(shownDays)}
            </p>
            <p className="text-lg sm:text-xl font-light" style={{ color: HUB_CREAM, opacity: 0.75, fontFamily: HUB_FONT_HEAD }}>
              {isKeys ? 'days · keys today!' : days === 1 ? 'day to your new keys' : 'days to your new keys'}
            </p>
          </div>
          <p className="mt-3 text-sm" style={{ color: HUB_CREAM, opacity: 0.6 }}>Settlement booked for {fmtDate(settlementDate)}</p>
        </div>
        <KeyRound className="w-14 h-14 flex-shrink-0" style={{ color: HUB_CREAM, opacity: 0.45 }} />
      </div>
    </div>
  );
};

// ─── Bento grid of status cards ────────────────────────────────────
const BentoGrid = ({ client, pendingDocs, docs, onGoTab }) => {
  const items = [
    {
      key: 'status', label: "Where we're at", value: client.stage_label || '—',
      icon: CheckCircle2, tone: 'ok',
    },
    {
      key: 'docs', label: 'What we need from you',
      value: pendingDocs > 0 ? `${pendingDocs} to upload` : (docs.length ? 'All complete' : 'Nothing yet'),
      icon: FileText, tone: pendingDocs > 0 ? 'warn' : 'ok',
      onClick: () => onGoTab('docs'),
    },
    {
      key: 'broker-eta', label: 'Next broker update',
      value: fmtDate(client.expected_broker_update),
      icon: Clock, tone: 'default',
    },
    {
      key: 'bank-eta', label: 'Bank should reply by',
      value: fmtDate(client.expected_bank_response),
      icon: Building2, tone: 'default',
    },
    {
      key: 'idv', label: 'Identity check',
      value: client.id_verification?.status_label || (client.id_verification_vid ? 'Completed' : 'Not started'),
      icon: ShieldCheck,
      tone: client.id_verification?.status_key === 'report_available' || client.id_verification?.status_key === 'completed' ? 'ok' : 'default',
    },
    {
      key: 'loan', label: 'Your home loan at a glance',
      value: client.loan?.lender || 'Being prepared',
      icon: Wallet, tone: 'default',
      onClick: () => onGoTab('loan'),
    },
  ];
  const toneStyles = {
    default: { bg: '#fff', border: `${HUB_NAVY}10`, badge: null },
    ok:      { bg: '#fff', border: `${HUB_NAVY}10`, badge: '#22C55E' },
    warn:    { bg: '#FEF3C7', border: '#FCD34D', badge: '#F59E0B' },
  };
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3" data-testid="hub-bento-grid">
      {items.map(({ key, label, value, icon: Icon, tone, onClick }) => {
        const t = toneStyles[tone] || toneStyles.default;
        return (
          <button
            key={key}
            type="button"
            onClick={onClick || (() => {})}
            data-testid={`hub-bento-${key}`}
            className="rounded-2xl p-4 sm:p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg group"
            style={{ backgroundColor: t.bg, border: `1px solid ${t.border}` }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${HUB_NAVY}0A` }}>
                <Icon className="w-4 h-4" style={{ color: HUB_NAVY }} />
              </div>
              {t.badge && <span className="w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: t.badge }} />}
            </div>
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>{label}</p>
            <p className="mt-1 text-sm sm:text-base font-medium leading-snug break-words" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{value}</p>
          </button>
        );
      })}
    </div>
  );
};

// ─── Action Card (when broker set a next_action) ───────────────────
const ActionCard = ({ action, onGoTab }) => (
  <div
    data-testid="hub-action-card"
    className="rounded-3xl p-5 sm:p-6 flex items-start gap-4"
    style={{ backgroundColor: '#FEF3C7', border: '2px solid #F59E0B' }}
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F59E0B' }}>
      <Zap className="w-5 h-5" style={{ color: HUB_NAVY }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: '#92400E' }}>Your next step</p>
      <p className="mt-1 text-base font-medium leading-snug" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{action}</p>
    </div>
    <button
      type="button"
      onClick={() => onGoTab('docs')}
      className="rounded-full text-xs font-semibold px-4 py-2 flex-shrink-0 transition-transform hover:scale-105"
      style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
    >
      Take me there
    </button>
  </div>
);

// ─── Expected updates ──────────────────────────────────────────────
const ExpectedUpdatesCard = ({ client }) => (
  <SoftCard>
    <SectionLabel icon={Clock}>When to expect updates</SectionLabel>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <div className="rounded-2xl p-4" style={{ backgroundColor: `${HUB_NAVY}06`, border: `1px solid ${HUB_NAVY}10` }}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Bank response</p>
        <p className="text-lg font-medium mt-1" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{fmtDate(client.expected_bank_response)}</p>
      </div>
      <div className="rounded-2xl p-4" style={{ backgroundColor: `${HUB_NAVY}06`, border: `1px solid ${HUB_NAVY}10` }}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Broker update</p>
        <p className="text-lg font-medium mt-1" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{fmtDateTime(client.expected_broker_update)}</p>
      </div>
    </div>
  </SoftCard>
);

// ─── Reusable soft card ────────────────────────────────────────────
const SoftCard = ({ children, className = '' }) => (
  <div
    className={`rounded-3xl p-5 sm:p-6 transition-all duration-300 ${className}`}
    style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}10`, boxShadow: '0 1px 3px rgba(21,19,58,0.03)' }}
  >
    {children}
  </div>
);

const SectionLabel = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2">
    {Icon && (
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${HUB_NAVY}0A` }}>
        <Icon className="w-3.5 h-3.5" style={{ color: HUB_NAVY }} />
      </div>
    )}
    <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>{children}</p>
  </div>
);

// ─── Stage timeline ────────────────────────────────────────────────
const StageTimeline = ({ currentStage, stages, stageLabels, stageUpdatedAt, stageNotes }) => {
  const currentIdx = stages.indexOf(currentStage);
  return (
    <SoftCard>
      <SectionLabel icon={TrendingUp}>Your loan journey</SectionLabel>
      <div className="mt-4 space-y-0">
        {stages.map((s, i) => {
          const Icon = STAGE_ICONS[s] || CheckCircle2;
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={s} className="flex items-start gap-3 relative">
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    backgroundColor: active ? HUB_NAVY : done ? '#22C55E' : `${HUB_NAVY}12`,
                    color: (active || done) ? HUB_CREAM : `${HUB_NAVY}80`,
                    boxShadow: active ? `0 0 0 4px ${HUB_NAVY}12` : 'none',
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                {i < stages.length - 1 && (
                  <div
                    className="w-0.5 flex-1 min-h-[24px]"
                    style={{ backgroundColor: (done || active) ? '#86EFAC' : `${HUB_NAVY}18` }}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0 pb-4">
                <p
                  className="text-sm leading-tight"
                  style={{
                    color: active ? HUB_NAVY : done ? '#059669' : `${HUB_NAVY}75`,
                    fontWeight: active ? 600 : done ? 500 : 400,
                    fontFamily: HUB_FONT_HEAD,
                  }}
                >
                  {stageLabels[s] || s}
                </p>
                {active && (
                  <div className="mt-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#059669' }}>Current stage</p>
                    <p className="text-[11px]" style={{ color: HUB_NAVY, opacity: 0.6 }}>{fmtDateTime(stageUpdatedAt)}</p>
                    {stageNotes && <p className="text-xs mt-1 italic" style={{ color: HUB_NAVY, opacity: 0.75 }}>{stageNotes}</p>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SoftCard>
  );
};

// ─── Activity feed ─────────────────────────────────────────────────
const ActivityFeed = ({ updates }) => (
  <SoftCard>
    <SectionLabel icon={MessageSquare}>Latest from your broker</SectionLabel>
    {updates.length === 0 && (
      <div className="mt-4 flex flex-col items-center py-6 text-center rounded-2xl" style={{ backgroundColor: `${HUB_NAVY}05` }}>
        <MessageSquare className="w-8 h-8 mb-2" style={{ color: HUB_NAVY, opacity: 0.35 }} />
        <p className="text-sm" style={{ color: HUB_NAVY, opacity: 0.65 }}>No updates yet — your broker will post here as things move.</p>
      </div>
    )}
    <div className="mt-4 space-y-4">
      {updates.slice(0, 15).map((u) => (
        <div key={u.id} className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: u.posted_by_role === 'broker' ? HUB_NAVY : u.posted_by_role === 'customer' ? '#22C55E' : `${HUB_NAVY}12`,
              color: HUB_CREAM,
            }}
          >
            {u.posted_by_role === 'broker' ? <User className="w-4 h-4" /> :
              u.posted_by_role === 'customer' ? <MessageSquare className="w-4 h-4" /> :
              <Info className="w-4 h-4" style={{ color: HUB_NAVY }} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{u.title}</p>
            {u.message && <p className="text-sm mt-0.5 leading-snug" style={{ color: HUB_NAVY, opacity: 0.75 }}>{u.message}</p>}
            <p className="text-[10px] mt-1" style={{ color: HUB_NAVY, opacity: 0.5 }}>
              {u.posted_by_name || 'System'} · {fmtDateTime(u.posted_at)}
            </p>
            {u.action_required && (
              <div className="mt-2 rounded-xl p-2.5" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#92400E' }}>What you need to do</p>
                <p className="text-xs mt-0.5" style={{ color: HUB_NAVY }}>{u.action_required}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </SoftCard>
);

// ─── ID verify card ────────────────────────────────────────────────
const IdVerifyCard = ({ idv, token }) => {
  const stateTone = ({
    report_available: { bg: '#D1FAE5', text: '#065F46', dot: '#22C55E' },
    completed:        { bg: '#D1FAE5', text: '#065F46', dot: '#22C55E' },
    in_progress:      { bg: '#DBEAFE', text: '#1E40AF', dot: '#3B82F6' },
    not_started:      { bg: `${HUB_NAVY}12`, text: HUB_NAVY, dot: `${HUB_NAVY}70` },
    expired:          { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
    cancelled:        { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
  })[idv.status_key] || { bg: `${HUB_NAVY}12`, text: HUB_NAVY, dot: `${HUB_NAVY}70` };

  const downloadReport = async () => {
    try {
      const resp = await axios.get(`${API}/api/hub/public/${token}/id-verification/report`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Report not available');
    }
  };

  return (
    <SoftCard>
      <SectionLabel icon={ShieldCheck}>Identity check</SectionLabel>
      <div className="mt-4 flex items-start gap-3">
        <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stateTone.dot }} />
        <div className="flex-1 min-w-0">
          <p className="text-base font-medium" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{idv.status_label}</p>
          {idv.reference_number && (
            <p className="text-[11px] mt-0.5" style={{ color: HUB_NAVY, opacity: 0.6 }}>Reference · <span className="font-mono">{idv.reference_number}</span></p>
          )}
          {idv.submitted_at && <p className="text-[11px]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Submitted · {fmtDateTime(idv.submitted_at)}</p>}
          {idv.report_generated_at && <p className="text-[11px]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Report ready · {fmtDateTime(idv.report_generated_at)}</p>}
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ backgroundColor: stateTone.bg, color: stateTone.text }}
        >
          {idv.status_key.replace('_', ' ')}
        </span>
      </div>
      {idv.has_report && (
        <button
          type="button"
          onClick={downloadReport}
          data-testid="hub-idv-download-report"
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-semibold py-2.5 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
        >
          <FileText className="w-3.5 h-3.5" /> Download verification report
        </button>
      )}
    </SoftCard>
  );
};

// ─── Broker card + compose message ─────────────────────────────────
const BrokerCard = ({ client }) => (
  <SoftCard>
    <SectionLabel icon={User}>Your broker</SectionLabel>
    <div className="mt-4 flex items-center gap-4">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-semibold text-xl relative flex-shrink-0"
           style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }}>
        {(client.broker_name || '?').slice(0, 1)}
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white" style={{ backgroundColor: '#22C55E' }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-lg font-medium truncate" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{client.broker_name || '—'}</p>
        <p className="text-xs flex items-center gap-1.5 mt-0.5" style={{ color: '#059669' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#22C55E' }} />
          Working on your file today
        </p>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-2.5">
      {client.broker_email && (
        <a
          href={`mailto:${client.broker_email}`}
          data-testid="hub-broker-email"
          className="rounded-full text-xs font-semibold py-2.5 inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY, border: `1px solid ${HUB_NAVY}15` }}
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </a>
      )}
      {client.broker_phone && (
        <a
          href={`tel:${client.broker_phone}`}
          data-testid="hub-broker-call"
          className="rounded-full text-xs font-semibold py-2.5 inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </a>
      )}
    </div>
  </SoftCard>
);

const ComposeMessage = ({ token, refresh }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    let ok = false;
    try {
      await axios.post(`${API}/api/hub/public/${token}/message`, {
        category: 'message',
        title: 'Message to broker',
        message: text.trim(),
      });
      ok = true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally { setBusy(false); }
    if (ok) {
      setText('');
      toast.success('Message sent — your broker will reply here');
      refresh();
    }
  };
  return (
    <SoftCard>
      <SectionLabel icon={Send}>Send a note to your broker</SectionLabel>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Ask a question, share an update, or let us know if anything's changed…"
        data-testid="hub-message-text"
        className="mt-3 w-full px-4 py-3 rounded-2xl text-sm outline-none transition-all"
        style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}15`, color: HUB_NAVY, fontFamily: HUB_FONT_BODY }}
        onFocus={(e) => e.currentTarget.style.borderColor = HUB_NAVY}
        onBlur={(e) => e.currentTarget.style.borderColor = `${HUB_NAVY}15`}
      />
      <button
        type="button"
        onClick={send}
        disabled={busy || !text.trim()}
        data-testid="hub-message-send"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-all"
        style={{
          backgroundColor: (busy || !text.trim()) ? `${HUB_NAVY}20` : HUB_NAVY,
          color: (busy || !text.trim()) ? `${HUB_NAVY}60` : HUB_CREAM,
          cursor: (busy || !text.trim()) ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send to broker
      </button>
    </SoftCard>
  );
};

// ═══════════════════════════════════════════════════════════════════
// LOAN tab
// ═══════════════════════════════════════════════════════════════════
const LoanRow = ({ label, value }) => (
  <div className="grid grid-cols-2 gap-3 py-3" style={{ borderBottom: `1px solid ${HUB_NAVY}08` }}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>{label}</p>
    <p className="text-sm text-right break-words" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{value || '—'}</p>
  </div>
);

const LoanTab = ({ loan, client }) => {
  const anyDetails = loan && Object.values(loan).some((v) => v && (typeof v !== 'object' || Object.keys(v).length > 0));
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <SoftCard>
        <SectionLabel icon={Wallet}>Your home loan at a glance</SectionLabel>
        {!anyDetails && (
          <EmptyState
            icon={Compass}
            title="Your loan is being prepared"
            copy="Your broker is structuring the right loan for your goals. Details will land here as they're finalised."
          />
        )}
        {anyDetails && (
          <div className="mt-3">
            <LoanRow label="Lender" value={loan.lender} />
            <LoanRow label="Loan amount" value={loan.amount ? fmtCurrency(loan.amount) : null} />
            <LoanRow label="Balance at approval" value={loan.approved_balance ? fmtCurrency(loan.approved_balance) : null} />
            <LoanRow label="Current balance" value={loan.current_balance ? fmtCurrency(loan.current_balance) : null} />
            <LoanRow label="Property security" value={loan.property_address} />
            <LoanRow label="Purpose" value={loan.purpose} />
            <LoanRow label="Repayment type" value={loan.repayment_type} />
            <LoanRow label="Interest type" value={loan.interest_type} />
            <LoanRow label="Loan product" value={loan.product} />
            <LoanRow label="Interest rate" value={loan.rate ? `${loan.rate}%` : null} />
            <LoanRow label="Repayment amount" value={loan.repayment_amount ? fmtCurrency(loan.repayment_amount) : null} />
            <LoanRow label="Offset" value={loan.offset_details} />
            <LoanRow label="Redraw" value={loan.redraw_details} />
            <LoanRow label="Approval date" value={fmtDate(loan.approval_date)} />
            <LoanRow label="Settlement date" value={fmtDate(loan.settlement_date)} />
            <LoanRow label="Fixed-rate expiry" value={fmtDate(loan.fixed_expiry)} />
            <LoanRow label="Interest-only expiry" value={fmtDate(loan.interest_only_expiry)} />
          </div>
        )}
      </SoftCard>
      {loan.features && loan.features.length > 0 && (
        <SoftCard>
          <SectionLabel icon={Sparkles}>Key loan features</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {loan.features.map((f) => (
              <span key={f} className="px-3 py-1.5 rounded-full text-[11px] font-semibold"
                    style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY, border: `1px solid ${HUB_NAVY}15` }}>{f}</span>
            ))}
          </div>
        </SoftCard>
      )}
      {loan.broker_notes && (
        <SoftCard>
          <SectionLabel icon={MessageSquare}>Notes from your broker</SectionLabel>
          <p className="mt-3 text-sm whitespace-pre-line leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.85 }}>{loan.broker_notes}</p>
        </SoftCard>
      )}

      {/* Financial exploration tools — only when we have the essentials */}
      {loan.amount && loan.rate && (
        <>
          <RepaymentSimulator loan={loan} client={client} />
          <OffsetOptimiser loan={loan} />
          <AmortisationChart loan={loan} />
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DOCS tab
// ═══════════════════════════════════════════════════════════════════
const DocsTab = ({ docs, token, refresh }) => {
  if (docs.length === 0) {
    return (
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        <SoftCard>
          <EmptyState
            icon={CheckCircle2}
            title="All caught up"
            copy="We have everything we need from you right now. Your broker will add new requests here whenever they come up."
          />
        </SoftCard>
      </div>
    );
  }

  const outstanding = docs.filter((d) => d.status === 'requested' || d.status === 'needs_correction');
  const pending = docs.filter((d) => d.status === 'uploaded' || d.status === 'reviewed');
  const emailed = docs.filter((d) => d.status === 'sent_via_email');
  const done = docs.filter((d) => d.status === 'approved');
  const completed = docs.length - outstanding.length;
  const pct = docs.length ? Math.round((completed / docs.length) * 100) : 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <SoftCard>
        <SectionLabel icon={FileText}>Document progress</SectionLabel>
        <div className="mt-4 flex items-end justify-between gap-4">
          <div>
            <p data-testid="hub-doc-counter"
               className="text-4xl sm:text-5xl font-light tracking-tighter"
               style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }}>
              {completed}<span className="text-xl sm:text-2xl font-light" style={{ opacity: 0.55 }}> / {docs.length}</span>
            </p>
            <p className="text-xs uppercase tracking-[0.24em] font-semibold mt-1" style={{ color: HUB_NAVY, opacity: 0.55 }}>
              {pct === 100 ? 'All done' : `${pct}% complete`}
            </p>
          </div>
          {pct === 100 && (
            <div className="rounded-full px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5"
                 style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> All approved
            </div>
          )}
        </div>
        <div className="mt-4 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${HUB_NAVY}12` }}>
          <div className="h-full rounded-full transition-all duration-1000 ease-out"
               style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#22C55E' : HUB_NAVY }} />
        </div>
      </SoftCard>

      {outstanding.length > 0 && (
        <DocsGroup title="What we need from you" testId="hub-docs-outstanding" tone="warn" items={outstanding} token={token} refresh={refresh} />
      )}
      {pending.length > 0 && (
        <DocsGroup title="Uploaded — under review" testId="hub-docs-pending" tone="info" items={pending} token={token} refresh={refresh} />
      )}
      {emailed.length > 0 && (
        <DocsGroup title="Sent via email" testId="hub-docs-emailed" tone="purple" items={emailed} token={token} refresh={refresh} />
      )}
      {done.length > 0 && (
        <DocsGroup title="Approved" testId="hub-docs-done" tone="ok" items={done} token={token} refresh={refresh} />
      )}
    </div>
  );
};

const DocsGroup = ({ title, items, tone, token, refresh, testId }) => {
  const dotColor = { warn: '#F59E0B', info: '#3B82F6', purple: '#A855F7', ok: '#22C55E' }[tone] || `${HUB_NAVY}70`;
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>{title}</p>
        <span className="text-[10px] font-semibold" style={{ color: HUB_NAVY, opacity: 0.4 }}>{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((d) => <DocRow key={d.id} doc={d} token={token} refresh={refresh} />)}
      </div>
    </div>
  );
};

const DOC_STATUS_META = {
  requested:       { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D', label: 'Waiting on you' },
  uploaded:        { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD', label: 'Under review' },
  sent_via_email:  { bg: '#F3E8FF', text: '#6B21A8', border: '#D8B4FE', label: 'Sent via email' },
  reviewed:        { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD', label: 'Reviewed' },
  approved:        { bg: '#D1FAE5', text: '#065F46', border: '#86EFAC', label: 'Approved' },
  needs_correction:{ bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5', label: 'Needs update' },
};

const DocRow = ({ doc, token, refresh }) => {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [emailNote, setEmailNote] = useState('');
  const inputRef = useRef(null);
  const meta = DOC_STATUS_META[doc.status] || DOC_STATUS_META.requested;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/hub/public/${token}/documents/${doc.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${doc.name} uploaded`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const markEmailed = async () => {
    try {
      await axios.post(`${API}/api/hub/public/${token}/documents/${doc.id}/sent-email`, { note: emailNote });
      toast.success('Marked as sent via email');
      setEmailMode(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const canModify = doc.status === 'requested' || doc.status === 'needs_correction';

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div
      className="rounded-2xl p-4 transition-all duration-200"
      style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}10`, borderLeftWidth: 4, borderLeftColor: meta.border }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{doc.name}</p>
          {doc.description && <p className="text-xs mt-1 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>{doc.description}</p>}
          {doc.due_date && (
            <p className="text-[11px] mt-1.5 flex items-center gap-1" style={{ color: HUB_NAVY, opacity: 0.55 }}>
              <Clock className="w-3 h-3" /> Due {fmtDate(doc.due_date)}
            </p>
          )}
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ backgroundColor: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}
        >
          {meta.label}
        </span>
      </div>
      {doc.status === 'needs_correction' && doc.broker_notes && (
        <div className="mt-3 rounded-xl p-3" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#991B1B' }}>Broker note</p>
          <p className="text-xs mt-0.5" style={{ color: HUB_NAVY }}>{doc.broker_notes}</p>
        </div>
      )}
      {doc.sent_via_email_note && (
        <div className="mt-3 rounded-xl p-3" style={{ backgroundColor: '#F3E8FF', border: '1px solid #D8B4FE' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#6B21A8' }}>Email note</p>
          <p className="text-xs mt-0.5" style={{ color: HUB_NAVY }}>{doc.sent_via_email_note}</p>
        </div>
      )}
      {canModify && (
        <div className="mt-3">
          {!emailMode ? (
            <>
              {/* Drag-drop upload zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                data-testid={`hub-doc-dropzone-${doc.id}`}
                className="cursor-pointer rounded-xl px-4 py-3 flex items-center justify-center gap-2 text-xs font-semibold transition-all"
                style={{
                  backgroundColor: drag ? `${HUB_NAVY}0F` : '#fff',
                  border: `2px dashed ${drag ? HUB_NAVY : `${HUB_NAVY}25`}`,
                  color: HUB_NAVY,
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  data-testid={`hub-doc-upload-input-${doc.id}`}
                  onChange={(e) => upload(e.target.files?.[0])}
                />
                {uploading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="w-3.5 h-3.5" /> Drop file here or tap to upload</>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEmailMode(true)}
                className="mt-2 w-full text-xs font-medium py-2 rounded-full transition-colors"
                style={{ color: HUB_NAVY, opacity: 0.7 }}
              >
                Already sent this via email? Log it here
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder='e.g. "I emailed this to your broker today"'
                data-testid={`hub-doc-email-note-${doc.id}`}
                className="w-full px-3 py-2 rounded-xl text-xs outline-none"
                style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}20`, color: HUB_NAVY }}
              />
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setEmailMode(false)} className="rounded-full text-xs font-semibold py-2"
                        style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY }}>Cancel</button>
                <button type="button" onClick={markEmailed} data-testid={`hub-doc-email-submit-${doc.id}`}
                        className="rounded-full text-xs font-semibold py-2"
                        style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}>Mark sent</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// GOALS tab
// ═══════════════════════════════════════════════════════════════════
const GoalsTab = ({ goals }) => {
  const sections = [
    { key: 'short_term',       label: 'Short-term goals',      icon: Sparkles },
    { key: 'long_term',        label: 'Long-term goals',       icon: TrendingUp },
    { key: 'property',         label: 'Property',              icon: Home },
    { key: 'debt_reduction',   label: 'Debt reduction',        icon: Wallet },
    { key: 'refinance',        label: 'Refinance',             icon: Compass },
    { key: 'investment',       label: 'Investment',            icon: TrendingUp },
    { key: 'repayment_strategy', label: 'Repayment strategy',  icon: Target },
    { key: 'action_plan',      label: 'Action plan',           icon: Zap },
    { key: 'broker_notes',     label: 'Notes from broker',     icon: MessageSquare },
  ];
  const filled = sections.filter(({ key }) => (goals[key] || '').trim());
  const anyFilled = filled.length > 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {goals.review_date && (
        <div
          data-testid="hub-goals-next-review"
          className="rounded-3xl p-5 sm:p-6 flex items-center gap-4"
          style={{ backgroundImage: `linear-gradient(120deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`, color: HUB_CREAM }}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${HUB_CREAM}18` }}>
            <Calendar className="w-6 h-6" style={{ color: HUB_CREAM }} />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Next goals review</p>
            <p className="text-lg sm:text-xl font-medium mt-0.5" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }}>{fmtDate(goals.review_date)}</p>
          </div>
        </div>
      )}

      <SoftCard>
        <SectionLabel icon={Target}>Your goals</SectionLabel>
        <p className="text-xs mt-2 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>
          The blueprint your broker is building around your life — short-term wins, long-term freedom.
        </p>
      </SoftCard>

      {!anyFilled && (
        <SoftCard>
          <EmptyState
            icon={Compass}
            title="Let's map out your future"
            copy="Your broker will build this out with you during your strategy session — you'll see your short-term wins, long-term goals and repayment blueprint here."
          />
        </SoftCard>
      )}

      {anyFilled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filled.map(({ key, label, icon: Icon }) => (
            <SoftCard key={key} className="hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${HUB_NAVY}0A` }}>
                  <Icon className="w-4 h-4" style={{ color: HUB_NAVY }} />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>{label}</p>
              </div>
              <p className="text-sm whitespace-pre-line leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.9 }}>{goals[key]}</p>
            </SoftCard>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// HELP tab
// ═══════════════════════════════════════════════════════════════════
const DEFAULT_FAQ = [
  { question: 'What happens after I upload my documents?', answer: 'Your broker reviews everything within 24-48 hours, then structures the best loan options for your goals.' },
  { question: 'How long does approval take?', answer: 'Typically 5-10 business days from a complete submission to conditional approval, then 1-3 more days for formal approval.' },
  { question: 'What does conditional approval mean?', answer: 'The lender is willing to approve subject to conditions (usually valuation + verifying a couple of items). It\'s a strong sign your loan will go through.' },
  { question: 'What does formal approval mean?', answer: 'The lender has confirmed final approval — all conditions have been met and loan documents are next.' },
  { question: 'What is a valuation?', answer: 'The bank arranges an independent property valuation to confirm the market value matches the purchase/loan amount.' },
  { question: 'When will I hear from my broker?', answer: 'You\'ll see a "Next broker update" date on your dashboard. Your broker also posts every important milestone here.' },
  { question: 'What happens after settlement?', answer: 'Keep using this portal for rate reviews, refinances, and future finance requests — your broker checks in annually.' },
  { question: 'Can I upload documents after settlement?', answer: 'Yes — the Docs tab stays open for any future requests.' },
  { question: 'How do I request a loan review?', answer: 'Tap the "Send a request" button below and choose "Home loan review".' },
];

const HelpTab = ({ requestTypes, faqs, howtos, token, refresh }) => {
  const [reqOpen, setReqOpen] = useState(false);
  const [faqSearch, setFaqSearch] = useState('');
  const [faqCat, setFaqCat] = useState('');
  const faqList = (faqs && faqs.length > 0) ? faqs : DEFAULT_FAQ;

  const faqCategories = Array.from(new Set(faqList.map((f) => f.category).filter(Boolean)));

  const q = faqSearch.trim().toLowerCase();
  const filteredFaqs = faqList.filter((f) => {
    if (faqCat && f.category !== faqCat) return false;
    if (q) {
      const hay = `${f.question} ${f.answer}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <SoftCard>
        <SectionLabel icon={Sparkles}>Need something from your broker?</SectionLabel>
        <p className="text-xs mt-2 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>
          Send a request — a loan review, a document reissue, a meeting or just a question. Your broker will get back to you.
        </p>
        <button
          type="button"
          onClick={() => setReqOpen(true)}
          data-testid="hub-open-request"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
        >
          <Sparkles className="w-4 h-4" /> Send a request
        </button>
      </SoftCard>

      {reqOpen && (
        <RequestForm types={requestTypes} token={token} refresh={refresh} onClose={() => setReqOpen(false)} />
      )}

      {howtos && howtos.length > 0 && (
        <SoftCard>
          <SectionLabel icon={Compass}>How-to guides</SectionLabel>
          <div className="mt-4 space-y-2">
            {howtos.map((h) => (
              <details key={h.id} className="group rounded-2xl overflow-hidden" style={{ border: `1px solid ${HUB_NAVY}10` }} data-testid={`hub-howto-${h.id}`}>
                <summary className="cursor-pointer text-sm font-medium flex items-start gap-2 p-4 transition-colors" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>
                  <ChevronRight className="w-4 h-4 mt-0.5 transition-transform group-open:rotate-90 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p>{h.title}</p>
                    {h.category && <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mt-1" style={{ color: HUB_NAVY, opacity: 0.55 }}>{h.category}</p>}
                  </div>
                </summary>
                <div className="p-4 pt-0 ml-6 space-y-2">
                  <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: HUB_NAVY, opacity: 0.8 }}>{h.body}</p>
                  {h.video_url && (
                    <a href={h.video_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold px-3 py-1.5 transition-transform hover:scale-105"
                       style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}>
                      <Sparkles className="w-3 h-3" /> Watch video
                    </a>
                  )}
                </div>
              </details>
            ))}
          </div>
        </SoftCard>
      )}

      <SoftCard>
        <SectionLabel icon={HelpCircle}>Frequently asked</SectionLabel>
        <div className="relative mt-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: HUB_NAVY, opacity: 0.4 }} />
          <input
            type="text"
            value={faqSearch}
            onChange={(e) => setFaqSearch(e.target.value)}
            placeholder="Search anything…"
            data-testid="hub-faq-search"
            className="w-full pl-11 pr-3 py-3 rounded-2xl text-sm outline-none transition-all"
            style={{ backgroundColor: `${HUB_NAVY}05`, border: `1px solid ${HUB_NAVY}12`, color: HUB_NAVY }}
            onFocus={(e) => e.currentTarget.style.borderColor = HUB_NAVY}
            onBlur={(e) => e.currentTarget.style.borderColor = `${HUB_NAVY}12`}
          />
        </div>
        {faqCategories.length > 0 && (
          <div className="mt-3 flex items-center gap-1.5 overflow-x-auto pb-1">
            <button type="button" onClick={() => setFaqCat('')}
                    className="whitespace-nowrap rounded-full text-[11px] font-semibold px-3 py-1.5 transition-colors"
                    style={{ backgroundColor: !faqCat ? HUB_NAVY : `${HUB_NAVY}0A`, color: !faqCat ? HUB_CREAM : HUB_NAVY }}>
              All
            </button>
            {faqCategories.map((c) => (
              <button key={c} type="button" onClick={() => setFaqCat(faqCat === c ? '' : c)} data-testid={`hub-faq-cat-${c}`}
                      className="whitespace-nowrap rounded-full text-[11px] font-semibold px-3 py-1.5 transition-colors"
                      style={{ backgroundColor: faqCat === c ? HUB_NAVY : `${HUB_NAVY}0A`, color: faqCat === c ? HUB_CREAM : HUB_NAVY }}>
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="mt-4 space-y-2">
          {filteredFaqs.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: HUB_NAVY, opacity: 0.55 }}>No FAQs match — try a different search.</p>
          ) : filteredFaqs.map((f, idx) => (
            <details key={f.id || idx} className="group rounded-2xl p-4" style={{ backgroundColor: `${HUB_NAVY}04` }}>
              <summary className="cursor-pointer text-sm font-medium flex items-center gap-2" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>
                <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90 flex-shrink-0" />
                <span className="flex-1">{f.question}</span>
                {f.category && <span className="text-[10px] font-semibold uppercase tracking-[0.2em] flex-shrink-0" style={{ color: HUB_NAVY, opacity: 0.4 }}>{f.category}</span>}
              </summary>
              <p className="ml-5 mt-2 text-xs leading-relaxed whitespace-pre-line" style={{ color: HUB_NAVY, opacity: 0.8 }}>{f.answer}</p>
            </details>
          ))}
        </div>
      </SoftCard>
    </div>
  );
};

const RequestForm = ({ types, token, refresh, onClose }) => {
  const [type, setType] = useState('review');
  const [msg, setMsg] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const submit = async () => {
    if (!msg.trim()) { toast.error('Please describe your request'); return; }
    setBusy(true);
    let reqId = null;
    try {
      const { data } = await axios.post(`${API}/api/hub/public/${token}/requests`, {
        request_type: type, message: msg.trim(), urgency,
      });
      reqId = data?.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send request');
      setBusy(false);
      return;
    }
    if (reqId && files.length > 0) {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          await axios.post(`${API}/api/hub/public/${token}/requests/${reqId}/attach`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) { toast.error(`Failed to attach ${f.name}`); }
      }
    }
    setBusy(false);
    toast.success('Request sent — your broker will respond shortly');
    refresh();
    onClose();
  };

  const addFiles = (list) => { if (!list) return; setFiles((prev) => [...prev, ...Array.from(list)]); };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <SoftCard className="border-2" >
      <SectionLabel icon={Send}>New request</SectionLabel>
      <label className="block mt-4">
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>What&apos;s it about?</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="hub-request-type"
          className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}15`, color: HUB_NAVY }}
        >
          {Object.entries(types || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <label className="block mt-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Tell your broker</span>
        <textarea
          rows={4}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="What can we help with?"
          data-testid="hub-request-message"
          className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}15`, color: HUB_NAVY }}
        />
      </label>
      <div className="mt-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Urgency</p>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {['low', 'medium', 'high'].map((u) => (
            <button
              key={u} type="button" onClick={() => setUrgency(u)}
              className="rounded-full text-xs font-semibold py-2.5 capitalize transition-colors"
              style={{ backgroundColor: urgency === u ? HUB_NAVY : `${HUB_NAVY}0A`, color: urgency === u ? HUB_CREAM : HUB_NAVY }}
            >
              {u}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.6 }}>Supporting documents (optional)</p>
        <input ref={fileInputRef} type="file" multiple className="hidden"
               data-testid="hub-request-file-input"
               onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="hub-request-add-files"
                className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 rounded-xl text-xs font-semibold py-2.5 transition-colors"
                style={{ border: `2px dashed ${HUB_NAVY}25`, color: HUB_NAVY }}>
          <Upload className="w-3.5 h-3.5" /> Attach files
        </button>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg px-3 py-2 text-[11px]"
                  style={{ backgroundColor: `${HUB_NAVY}05`, border: `1px solid ${HUB_NAVY}10`, color: HUB_NAVY }}>
                <span className="truncate">{f.name} · {Math.round(f.size / 1024)} KB</span>
                <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700 text-[11px] font-semibold ml-2">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button type="button" onClick={onClose}
                className="rounded-full text-sm font-semibold py-3"
                style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY }}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy || !msg.trim()} data-testid="hub-request-submit"
                className="rounded-full text-sm font-semibold py-3 transition-all"
                style={{
                  backgroundColor: (busy || !msg.trim()) ? `${HUB_NAVY}20` : HUB_NAVY,
                  color: (busy || !msg.trim()) ? `${HUB_NAVY}70` : HUB_CREAM,
                }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Send request'}
        </button>
      </div>
    </SoftCard>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PROFILE tab
// ═══════════════════════════════════════════════════════════════════
const ProfileTab = ({ client, token, refresh }) => {
  const [email, setEmail] = useState(client.email || '');
  const [mobile, setMobile] = useState(client.mobile || '');
  const [prefs, setPrefs] = useState(client.communication_preferences || {});
  const [notif, setNotif] = useState(client.notification_preferences || {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/hub/public/${token}/profile`, {
        email, mobile,
        communication_preferences: prefs,
        notification_preferences: notif,
      });
      toast.success('Profile updated');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  const togglePref = (dict, setDict, key) => setDict({ ...dict, [key]: !dict[key] });

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <SoftCard>
        <SectionLabel icon={User}>Your details</SectionLabel>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Full name</p>
            <p className="text-base font-medium mt-1" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{client.first_name} {client.last_name}</p>
            <p className="text-[10px] mt-0.5" style={{ color: HUB_NAVY, opacity: 0.5 }}>Contact your broker to change your name</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Application reference</p>
            <p className="text-base font-mono mt-1" style={{ color: HUB_NAVY }}>{client.reference_number || '—'}</p>
          </div>
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="hub-profile-email"
                   className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                   style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}15`, color: HUB_NAVY }} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Mobile</span>
            <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="hub-profile-mobile"
                   className="mt-1.5 w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                   style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}15`, color: HUB_NAVY }} />
          </label>
        </div>
      </SoftCard>

      <SoftCard>
        <SectionLabel icon={PhoneCall}>How you&apos;d like your broker to reach you</SectionLabel>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { key: 'email', label: 'Email' },
            { key: 'sms', label: 'SMS' },
            { key: 'phone', label: 'Phone call' },
            { key: 'portal_only', label: 'Portal only' },
          ].map((p) => (
            <button key={p.key} type="button" onClick={() => togglePref(prefs, setPrefs, p.key)}
                    data-testid={`hub-profile-pref-${p.key}`}
                    className="rounded-full text-xs font-semibold px-4 py-2 transition-colors"
                    style={{
                      backgroundColor: prefs[p.key] ? HUB_NAVY : `${HUB_NAVY}0A`,
                      color: prefs[p.key] ? HUB_CREAM : HUB_NAVY,
                    }}>
              {p.label}
            </button>
          ))}
        </div>
      </SoftCard>

      <SoftCard>
        <SectionLabel icon={Bell}>What you want to be notified about</SectionLabel>
        <div className="mt-4 space-y-2">
          {[
            { key: 'doc_requests', label: 'When a new document is requested' },
            { key: 'stage_updates', label: 'When my application stage changes' },
            { key: 'broker_messages', label: 'When my broker posts an update' },
            { key: 'settlement', label: 'When settlement is booked' },
          ].map((p) => (
            <label key={p.key} className="flex items-center gap-3 text-sm cursor-pointer p-2 rounded-lg transition-colors"
                   style={{ color: HUB_NAVY }}
                   onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${HUB_NAVY}04`}
                   onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <input type="checkbox" checked={!!notif[p.key]} onChange={() => togglePref(notif, setNotif, p.key)}
                     data-testid={`hub-profile-notif-${p.key}`}
                     className="w-4 h-4 rounded" style={{ accentColor: HUB_NAVY }} />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      </SoftCard>

      <button type="button" onClick={save} disabled={busy} data-testid="hub-profile-save"
              className="w-full rounded-full text-sm font-semibold py-3.5 transition-all"
              style={{
                backgroundColor: busy ? `${HUB_NAVY}20` : HUB_NAVY,
                color: busy ? `${HUB_NAVY}70` : HUB_CREAM,
              }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save changes'}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// FINANCIAL EXPLORATION TOOLS (on the Loan tab)
// ═══════════════════════════════════════════════════════════════════

// Loan math helpers — all inputs in monthly units.
// Standard PMT formula. If r=0 (interest-free), payment is principal/months.
const _pmt = (principal, monthlyRate, months) => {
  if (monthlyRate === 0) return principal / months;
  const f = Math.pow(1 + monthlyRate, months);
  return (principal * monthlyRate * f) / (f - 1);
};
// Given a monthly payment, how many months to pay off principal? Returns Infinity if payment ≤ interest.
const _monthsToPayoff = (principal, monthlyRate, monthlyPayment) => {
  if (monthlyRate === 0) return principal / monthlyPayment;
  const interestOnly = principal * monthlyRate;
  if (monthlyPayment <= interestOnly) return Infinity;
  return Math.log(monthlyPayment / (monthlyPayment - interestOnly)) / Math.log(1 + monthlyRate);
};
// Amortisation schedule (month-by-month principal / interest / balance)
const _amortSchedule = (principal, monthlyRate, monthlyPayment, maxMonths = 480) => {
  const out = [];
  let bal = principal;
  for (let m = 1; m <= maxMonths; m++) {
    const interest = bal * monthlyRate;
    const pay = Math.min(monthlyPayment, bal + interest);
    const principalPaid = pay - interest;
    bal = Math.max(0, bal - principalPaid);
    out.push({ month: m, interest, principal: principalPaid, balance: bal, cumulative_interest: (out[out.length - 1]?.cumulative_interest || 0) + interest });
    if (bal <= 0.01) break;
  }
  return out;
};

const _loanEssentials = (loan) => {
  const principal = Number(loan.amount) || 0;
  const annualRate = Number(loan.rate) || 0;
  const monthlyRate = annualRate / 100 / 12;
  const termYears = Number(loan.loan_term_years) || 30;
  const months = termYears * 12;
  // Prefer broker-set repayment; otherwise compute
  let monthlyRepayment = Number(loan.repayment_amount) || 0;
  if (!monthlyRepayment && principal && monthlyRate) monthlyRepayment = _pmt(principal, monthlyRate, months);
  return { principal, annualRate, monthlyRate, termYears, months, monthlyRepayment };
};

// ─── Repayment simulator ───────────────────────────────────────────
const RepaymentSimulator = ({ loan, client }) => {
  const { principal, monthlyRate, months, monthlyRepayment } = _loanEssentials(loan);
  const [extraWeekly, setExtraWeekly] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  const stats = useMemo(() => {
    if (!principal || !monthlyRate || !monthlyRepayment) return null;
    const baselineTotalInterest = monthlyRepayment * months - principal;
    const extraMonthly = (extraWeekly * 52) / 12;
    const newPayment = monthlyRepayment + extraMonthly;
    const newMonths = _monthsToPayoff(principal, monthlyRate, newPayment);
    if (!isFinite(newMonths)) return null;
    const newTotalInterest = newPayment * newMonths - principal;
    return {
      newMonths,
      newYears: newMonths / 12,
      yearsSaved: (months - newMonths) / 12,
      monthsSaved: months - newMonths,
      interestSaved: Math.max(0, baselineTotalInterest - newTotalInterest),
      baselineTotalInterest,
      newTotalInterest,
      newPayment,
      extraMonthly,
    };
  }, [principal, monthlyRate, months, monthlyRepayment, extraWeekly]);

  if (!stats) return null;

  const canShare = extraWeekly > 0 && stats.yearsSaved >= 0.2 && stats.interestSaved > 1000;

  return (
    <SoftCard>
      <SectionLabel icon={Calculator}>What if you paid extra?</SectionLabel>
      <p className="text-xs mt-2 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>
        Slide to see what putting a little extra into your loan each week would do. Even small amounts compound massively over a 30-year loan.
      </p>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Extra per week</span>
          <span className="text-2xl font-light tracking-tight" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.02em' }} data-testid="hub-sim-extra-weekly">
            {fmtCurrency(extraWeekly)}
          </span>
        </div>
        <input
          type="range" min="0" max="1000" step="25"
          value={extraWeekly}
          onChange={(e) => setExtraWeekly(Number(e.target.value))}
          data-testid="hub-sim-slider"
          className="w-full mt-2 accent-slate-900"
          style={{ accentColor: HUB_NAVY }}
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: HUB_NAVY, opacity: 0.5 }}>
          <span>$0</span><span>$500</span><span>$1,000</span>
        </div>
      </div>

      {extraWeekly > 0 && stats.yearsSaved > 0 && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-2xl p-4" style={{ backgroundColor: '#D1FAE5', border: '1px solid #86EFAC' }}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#065F46' }}>Years saved</p>
              <p className="text-3xl font-light tracking-tighter mt-1" style={{ color: '#065F46', fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }} data-testid="hub-sim-years-saved">
                {stats.yearsSaved.toFixed(1)}
                <span className="text-sm font-light ml-1" style={{ opacity: 0.7 }}>years</span>
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#065F46', opacity: 0.75 }}>
                Loan paid off in {stats.newYears.toFixed(1)} years instead of {(months / 12).toFixed(0)}.
              </p>
            </div>
            <div className="rounded-2xl p-4" style={{ backgroundImage: `linear-gradient(135deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`, color: HUB_CREAM }}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Interest saved</p>
              <p className="text-3xl font-light tracking-tighter mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }} data-testid="hub-sim-interest-saved">
                {fmtCurrency(Math.round(stats.interestSaved))}
              </p>
              <p className="text-[11px] mt-1" style={{ color: HUB_CREAM, opacity: 0.7 }}>
                For only {fmtCurrency(Math.round(stats.extraMonthly))} extra per month.
              </p>
            </div>
          </div>
          {canShare && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              data-testid="hub-sim-share-button"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-transform hover:scale-[1.01]"
              style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
            >
              <Share2 className="w-4 h-4" /> Share this with your partner
            </button>
          )}
        </>
      )}
      {extraWeekly === 0 && (
        <p className="mt-5 text-xs italic text-center" style={{ color: HUB_NAVY, opacity: 0.5 }}>Try sliding right — even $25/week pays off years.</p>
      )}

      {shareOpen && (
        <SharePartnerDialog
          client={client}
          stats={stats}
          extraWeekly={extraWeekly}
          onClose={() => setShareOpen(false)}
        />
      )}
    </SoftCard>
  );
};

// ─── Shareable image dialog + canvas renderer ──────────────────────
const SharePartnerDialog = ({ client, stats, extraWeekly, onClose }) => {
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const lastName = (client?.last_name || '').trim();
  const firstName = (client?.first_name || '').trim();
  const familyLabel = lastName ? `The ${lastName}${lastName.endsWith('s') ? '' : 's'}` : (firstName ? `${firstName}'s family` : 'Your family');

  const interestSavedRounded = Math.round(stats.interestSaved);
  const yearsSaved = stats.yearsSaved.toFixed(1);

  // Draw the shareable image on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1200, H = 630;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, HUB_NAVY);
    grad.addColorStop(0.5, HUB_NAVY_LIGHT);
    grad.addColorStop(1, HUB_NAVY_DARK);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Dot texture
    ctx.fillStyle = HUB_CREAM;
    ctx.globalAlpha = 0.045;
    for (let y = 15; y < H; y += 22) {
      for (let x = 15; x < W; x += 22) {
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    // Soft corner glow
    const glow = ctx.createRadialGradient(W - 100, 100, 30, W - 100, 100, 400);
    glow.addColorStop(0, HUB_NAVY_LIGHT + 'CC');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // Corner accent lines
    ctx.strokeStyle = HUB_CREAM + '40';
    ctx.lineWidth = 1;
    ctx.strokeRect(60, 60, W - 120, H - 120);

    // Draw logo (async but we can await via Image)
    const drawText = () => {
      // Small caps eyebrow
      ctx.font = '500 20px Outfit, sans-serif';
      ctx.fillStyle = HUB_CREAM + 'A0';
      ctx.textAlign = 'left';
      ctx.fillText('YOUR DERWENT LOAN JOURNEY', 90, 165);

      // Family + intro line
      ctx.font = '300 44px Outfit, sans-serif';
      ctx.fillStyle = HUB_CREAM;
      ctx.fillText(`${familyLabel} would save`, 90, 230);

      // BIG interest saved number
      ctx.font = '300 148px Outfit, sans-serif';
      ctx.fillStyle = HUB_CREAM;
      ctx.textBaseline = 'top';
      ctx.fillText(
        new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(interestSavedRounded),
        90, 250
      );
      ctx.textBaseline = 'alphabetic';

      // Supporting line
      ctx.font = '400 30px Manrope, sans-serif';
      ctx.fillStyle = HUB_CREAM + 'CC';
      ctx.fillText(
        `in interest by paying just $${extraWeekly}/week extra —`,
        90, 445
      );
      ctx.fillText(
        `and paying off the loan ${yearsSaved} years sooner.`,
        90, 485
      );

      // Bottom row: badge + tagline
      ctx.fillStyle = HUB_CREAM;
      ctx.font = '600 16px Manrope, sans-serif';
      ctx.fillText('POWERED BY MY DERWENT HUB', 90, 555);

      ctx.textAlign = 'right';
      ctx.font = '400 20px Outfit, sans-serif';
      ctx.fillStyle = HUB_CREAM + 'B0';
      ctx.fillText('Ask your broker how →', W - 90, 555);
      ctx.textAlign = 'left';

      // Export as data URL
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setImgUrl(url);
        }
      }, 'image/png');
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Draw logo on cream tile top-left
      const logoH = 72;
      const logoW = (img.width / img.height) * logoH;
      const padX = 20, padY = 16;
      // Cream tile
      ctx.fillStyle = HUB_CREAM;
      ctx.beginPath();
      const tileW = logoW + padX * 2, tileH = logoH + padY * 2;
      const rx = 16;
      ctx.moveTo(90 + rx, 60);
      ctx.arcTo(90 + tileW, 60, 90 + tileW, 60 + tileH, rx);
      ctx.arcTo(90 + tileW, 60 + tileH, 90, 60 + tileH, rx);
      ctx.arcTo(90, 60 + tileH, 90, 60, rx);
      ctx.arcTo(90, 60, 90 + tileW, 60, rx);
      ctx.closePath();
      ctx.fill();
      ctx.drawImage(img, 90 + padX, 60 + padY, logoW, logoH);
      drawText();
    };
    img.onerror = drawText;  // fallback: skip logo
    img.src = '/my-derwent-hub-logo.png';
  }, [familyLabel, interestSavedRounded, yearsSaved, extraWeekly]);

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = `derwent-savings-${lastName || 'family'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Image downloaded — share it with your partner!');
  };

  const share = async () => {
    if (!canvasRef.current) return;
    if (navigator.share && navigator.canShare) {
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) return download();
        const file = new File([blob], 'derwent-savings.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: `${familyLabel} would save ${fmtCurrency(interestSavedRounded)}`,
              text: `We could save ${fmtCurrency(interestSavedRounded)} in interest and pay off ${yearsSaved} years sooner by paying $${extraWeekly}/week extra. Come talk to our broker at Derwent Finance.`,
            });
          } catch (e) {
            if (e.name !== 'AbortError') download();
          }
        } else {
          download();
        }
      }, 'image/png');
    } else {
      download();
    }
  };

  const copyLine = async () => {
    const line = `${familyLabel} would save ${fmtCurrency(interestSavedRounded)} in interest and pay off ${yearsSaved} years sooner by paying $${extraWeekly}/week extra. Ask about it at Derwent Finance.`;
    try {
      await navigator.clipboard.writeText(line);
      setCopied(true);
      toast.success('Message copied — paste it into a text!');
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: `${HUB_NAVY}CC`, backdropFilter: 'blur(8px)' }}
         onClick={onClose}
         data-testid="hub-share-partner-dialog">
      <div className="w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
           style={{ backgroundColor: '#fff' }}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${HUB_NAVY}10` }}>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Share with your partner</p>
            <p className="text-lg font-medium mt-0.5" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>Your savings, ready to share</p>
          </div>
          <button type="button" onClick={onClose} data-testid="hub-share-close"
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                  style={{ color: HUB_NAVY }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {/* Preview */}
          <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${HUB_NAVY}12`, backgroundColor: `${HUB_NAVY}05` }}>
            {imgUrl ? (
              <img src={imgUrl} alt="Your savings" className="w-full h-auto" data-testid="hub-share-preview" />
            ) : (
              <div className="flex items-center justify-center h-40 text-sm" style={{ color: HUB_NAVY, opacity: 0.55 }}>
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Rendering your image…
              </div>
            )}
          </div>
          {/* Hidden canvas we render onto */}
          <canvas ref={canvasRef} className="hidden" />

          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={share}
              disabled={!imgUrl}
              data-testid="hub-share-native"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-transform hover:scale-[1.01] disabled:opacity-60"
              style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
            >
              <Share2 className="w-4 h-4" /> Share image
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={download}
                disabled={!imgUrl}
                data-testid="hub-share-download"
                className="inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-semibold py-2.5 transition-colors disabled:opacity-60"
                style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY, border: `1px solid ${HUB_NAVY}15` }}
              >
                <Download className="w-3.5 h-3.5" /> Download PNG
              </button>
              <button
                type="button"
                onClick={copyLine}
                data-testid="hub-share-copy-text"
                className="inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-semibold py-2.5 transition-colors"
                style={{ backgroundColor: `${HUB_NAVY}0A`, color: HUB_NAVY, border: `1px solid ${HUB_NAVY}15` }}
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Copy text'}
              </button>
            </div>
          </div>

          <p className="mt-4 text-[11px] leading-relaxed text-center" style={{ color: HUB_NAVY, opacity: 0.55 }}>
            The image is generated privately on your device. Nothing is sent anywhere until you tap share.
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Offset optimiser ──────────────────────────────────────────────
const OffsetOptimiser = ({ loan }) => {
  const { principal, monthlyRate, months, monthlyRepayment, annualRate } = _loanEssentials(loan);
  const [offset, setOffset] = useState(0);

  const stats = useMemo(() => {
    if (!principal || !monthlyRate || !monthlyRepayment) return null;
    if (offset >= principal) return null;
    // Interest saved this year vs no offset (rough) = offset * annual rate
    const annualSaving = offset * (annualRate / 100);
    const monthlySaving = annualSaving / 12;
    // Years cut off — using the offset amount as if it were a lump-sum extra repayment against the effective balance.
    const effectivePrincipal = principal - offset;
    const monthsWithOffset = _monthsToPayoff(effectivePrincipal, monthlyRate, monthlyRepayment);
    const yearsSaved = (months - monthsWithOffset) / 12;
    // If offset were in a 4% savings account instead, it would earn (offset * 0.04) per year in taxable interest (~30% MTR = 2.8% net).
    // Net benefit of offset vs savings = offset * (rate - after-tax savings rate).
    const savingsRateNet = 0.028;
    const offsetNetBenefit = offset * ((annualRate / 100) - savingsRateNet);
    return {
      annualSaving,
      monthlySaving,
      yearsSaved,
      offsetNetBenefit,
    };
  }, [principal, monthlyRate, months, monthlyRepayment, annualRate, offset]);

  return (
    <SoftCard>
      <SectionLabel icon={PiggyBank}>Offset optimiser</SectionLabel>
      <p className="text-xs mt-2 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>
        Every dollar in your offset earns you the equivalent of your home-loan rate — tax-free. Slide to see how much yours could be quietly saving you.
      </p>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Offset balance</span>
          <span className="text-2xl font-light tracking-tight" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.02em' }} data-testid="hub-offset-amount">
            {fmtCurrency(offset)}
          </span>
        </div>
        <input
          type="range" min="0" max={Math.round(Math.min(principal, 500000) / 5000) * 5000} step="5000"
          value={offset}
          onChange={(e) => setOffset(Number(e.target.value))}
          data-testid="hub-offset-slider"
          className="w-full mt-2"
          style={{ accentColor: HUB_NAVY }}
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: HUB_NAVY, opacity: 0.5 }}>
          <span>$0</span>
          <span>{fmtCurrency(Math.round(Math.min(principal, 500000) / 2 / 1000) * 1000)}</span>
          <span>{fmtCurrency(Math.round(Math.min(principal, 500000) / 1000) * 1000)}</span>
        </div>
      </div>

      {stats && offset > 0 && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl p-4" style={{ backgroundColor: `${HUB_NAVY}0A`, border: `1px solid ${HUB_NAVY}15` }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Interest saved / yr</p>
            <p className="text-2xl font-light tracking-tighter mt-1" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }} data-testid="hub-offset-annual-saving">
              {fmtCurrency(Math.round(stats.annualSaving))}
            </p>
            <p className="text-[11px] mt-1" style={{ color: HUB_NAVY, opacity: 0.55 }}>≈ {fmtCurrency(Math.round(stats.monthlySaving))}/month</p>
          </div>
          <div className="rounded-2xl p-4" style={{ backgroundColor: '#D1FAE5', border: '1px solid #86EFAC' }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#065F46' }}>Years off the loan</p>
            <p className="text-2xl font-light tracking-tighter mt-1" style={{ color: '#065F46', fontFamily: HUB_FONT_HEAD }} data-testid="hub-offset-years-saved">
              {stats.yearsSaved.toFixed(1)}
            </p>
            <p className="text-[11px] mt-1" style={{ color: '#065F46', opacity: 0.75 }}>if the offset stayed at this level</p>
          </div>
          <div className="rounded-2xl p-4" style={{ backgroundImage: `linear-gradient(135deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`, color: HUB_CREAM }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>vs. savings account</p>
            <p className="text-2xl font-light tracking-tighter mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }} data-testid="hub-offset-vs-savings">
              +{fmtCurrency(Math.round(stats.offsetNetBenefit))}
            </p>
            <p className="text-[11px] mt-1" style={{ color: HUB_CREAM, opacity: 0.7 }}>net benefit / yr vs 4% savings (after tax)</p>
          </div>
        </div>
      )}
      {offset === 0 && (
        <p className="mt-5 text-xs italic text-center" style={{ color: HUB_NAVY, opacity: 0.5 }}>Slide right to see the offset magic.</p>
      )}
    </SoftCard>
  );
};

// ─── Amortisation visualiser ───────────────────────────────────────
const AmortisationChart = ({ loan }) => {
  const { principal, monthlyRate, months, monthlyRepayment } = _loanEssentials(loan);

  const schedule = useMemo(() => {
    if (!principal || !monthlyRate || !monthlyRepayment) return [];
    return _amortSchedule(principal, monthlyRate, monthlyRepayment, months);
  }, [principal, monthlyRate, months, monthlyRepayment]);

  if (schedule.length === 0) return null;

  const totalInterest = schedule.reduce((s, m) => s + m.interest, 0);
  const totalPaid = totalInterest + principal;
  const interestRatio = totalInterest / totalPaid;

  // Sample down to ~12 points per year (yearly) for the SVG chart
  const yearly = [];
  for (let y = 1; y <= Math.ceil(schedule.length / 12); y++) {
    const monthIdx = Math.min(schedule.length - 1, y * 12 - 1);
    const row = schedule[monthIdx];
    yearly.push({
      year: y,
      balance: row.balance,
      cumulative_interest: row.cumulative_interest,
      cumulative_principal: principal - row.balance,
    });
  }

  const W = 640, H = 200;
  const maxV = Math.max(...yearly.map((y) => y.cumulative_principal + y.cumulative_interest));
  const step = W / (yearly.length - 1 || 1);

  // Cumulative principal area (top layer)
  const principalPath = yearly.map((y, i) => `${i * step},${H - (y.cumulative_principal / maxV) * H}`);
  // Cumulative total = principal + interest (upper boundary of interest layer)
  const totalPath = yearly.map((y, i) => `${i * step},${H - ((y.cumulative_principal + y.cumulative_interest) / maxV) * H}`);

  const principalArea = `M0,${H} L${principalPath.join(' L')} L${W},${H} Z`;
  const interestArea = `M${principalPath[0]} L${principalPath.join(' L')} L${totalPath.slice().reverse().join(' L')} Z`;

  return (
    <SoftCard>
      <SectionLabel icon={LineChart}>Where every dollar goes over 30 years</SectionLabel>
      <p className="text-xs mt-2 leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.7 }}>
        Most people never see this. Here&apos;s what you actually pay over the life of the loan — principal vs interest.
      </p>

      <div className="mt-5 rounded-2xl p-4" style={{ backgroundColor: `${HUB_NAVY}04`, border: `1px solid ${HUB_NAVY}10` }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none" data-testid="hub-amort-chart">
          <path d={principalArea} fill={HUB_NAVY} opacity="0.85" />
          <path d={interestArea} fill="#F59E0B" opacity="0.75" />
          <polyline points={principalPath.join(' ')} fill="none" stroke={HUB_CREAM} strokeWidth="1.5" />
        </svg>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: HUB_NAVY }} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Principal (what you keep)</span>
            </div>
            <p className="text-lg font-medium mt-1" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }} data-testid="hub-amort-principal-total">{fmtCurrency(Math.round(principal))}</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Interest (cost of borrowing)</span>
            </div>
            <p className="text-lg font-medium mt-1" style={{ color: '#B45309', fontFamily: HUB_FONT_HEAD }} data-testid="hub-amort-interest-total">{fmtCurrency(Math.round(totalInterest))}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl p-4" style={{ backgroundImage: `linear-gradient(135deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`, color: HUB_CREAM }}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Total you&apos;ll pay</p>
        <p className="text-3xl sm:text-4xl font-light tracking-tighter mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }} data-testid="hub-amort-total-paid">
          {fmtCurrency(Math.round(totalPaid))}
        </p>
        <p className="text-sm mt-2" style={{ color: HUB_CREAM, opacity: 0.75 }}>
          That&apos;s <strong>{(interestRatio * 100).toFixed(0)}%</strong> of your payments going to interest.
          Every extra dollar you throw at the principal early swings that ratio hugely in your favour — try the simulator above.
        </p>
      </div>
    </SoftCard>
  );
};

// ─── Shared empty state ────────────────────────────────────────────
const EmptyState = ({ icon: Icon, title, copy }) => (
  <div className="flex flex-col items-center text-center py-8 sm:py-10 px-4" data-testid="hub-empty-state">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
         style={{ backgroundColor: `${HUB_NAVY}0A`, border: `1px solid ${HUB_NAVY}15` }}>
      <Icon className="w-8 h-8" style={{ color: HUB_NAVY, opacity: 0.55 }} />
    </div>
    <p className="text-lg font-medium" style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD }}>{title}</p>
    <p className="text-sm mt-2 max-w-sm leading-relaxed" style={{ color: HUB_NAVY, opacity: 0.65 }}>{copy}</p>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// POST-SETTLEMENT RETENTION WIDGETS
// ═══════════════════════════════════════════════════════════════════

// ─── Anniversary review nudge ──────────────────────────────────────
const AnniversaryBanner = ({ review, token, refresh, client }) => {
  const [busy, setBusy] = useState(false);
  const book = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/hub/public/${token}/book-annual-review`);
      toast.success('Annual review requested — your broker will be in touch');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to book review');
    } finally { setBusy(false); }
  };
  const months = review.months_since_last_review ?? review.months_since_settlement ?? 0;
  const broker = (client?.broker_name || 'your broker').split(' ')[0];
  return (
    <div
      data-testid="hub-anniversary-banner"
      className="rounded-3xl p-5 sm:p-6 relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(120deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`,
        color: HUB_CREAM,
      }}
    >
      <div className="absolute -top-16 -right-10 w-52 h-52 rounded-full pointer-events-none opacity-30"
           style={{ backgroundColor: HUB_NAVY_LIGHT, filter: 'blur(50px)' }} />
      <div className="relative flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${HUB_CREAM}18` }}>
          <CalendarClock className="w-6 h-6" style={{ color: HUB_CREAM }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Your annual review is due</p>
          <p className="text-lg sm:text-xl font-medium mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }}>
            It&apos;s been {months} months since your loan was last reviewed
          </p>
          <p className="text-sm mt-2" style={{ color: HUB_CREAM, opacity: 0.75 }}>
            Rates, your equity and your goals all move — {broker} will make sure your loan is still the best fit and see if there&apos;s money to save.
          </p>
          <button
            type="button"
            onClick={book}
            disabled={busy}
            data-testid="hub-book-annual-review"
            className="mt-4 inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: HUB_CREAM, color: HUB_NAVY }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
            Book my annual review
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Property value tracker ────────────────────────────────────────
const PropertyValueCard = ({ valuation }) => {
  const shownValue = useCountUp(valuation.current_value || 0, 1100);
  const pct = valuation.appreciation_pct;
  const positive = (valuation.appreciation || 0) >= 0;
  return (
    <div
      data-testid="hub-property-value-card"
      className="rounded-3xl p-6 sm:p-7 relative overflow-hidden"
      style={{ backgroundColor: '#fff', border: `1px solid ${HUB_NAVY}12`, boxShadow: '0 1px 3px rgba(21,19,58,0.04)' }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Your property is now worth</p>
          <p
            className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter"
            style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }}
            data-testid="hub-property-value"
          >
            {fmtCurrency(Math.round(shownValue))}
          </p>
          {valuation.appreciation !== null && valuation.appreciation !== undefined && (
            <div className="mt-3 flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{
                  backgroundColor: positive ? '#D1FAE5' : '#FEE2E2',
                  color: positive ? '#065F46' : '#991B1B',
                }}
              >
                {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5 rotate-180" />}
                {positive ? '+' : ''}{fmtCurrency(Math.round(valuation.appreciation))}
                {pct != null && ` · ${positive ? '+' : ''}${pct}%`}
              </span>
              <span className="text-xs" style={{ color: HUB_NAVY, opacity: 0.55 }}>since you bought</span>
            </div>
          )}
          <p className="text-[11px] mt-3 flex items-center gap-1.5" style={{ color: HUB_NAVY, opacity: 0.5 }}>
            <RefreshCw className="w-3 h-3" />
            {valuation.source} · updated {fmtDate(valuation.valuation_date)}
          </p>
        </div>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${HUB_NAVY}0A` }}>
          <Home className="w-6 h-6" style={{ color: HUB_NAVY }} />
        </div>
      </div>
      {valuation.notes && (
        <p className="mt-4 text-sm italic" style={{ color: HUB_NAVY, opacity: 0.7 }}>{valuation.notes}</p>
      )}
      {valuation.history && valuation.history.length >= 2 && (
        <MiniTrendChart history={[...valuation.history, { date: valuation.valuation_date, value: valuation.current_value }]} />
      )}
    </div>
  );
};

const MiniTrendChart = ({ history }) => {
  // Simple SVG sparkline from history entries
  const values = history.map((h) => h.value).filter((v) => typeof v === 'number');
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 300;
  const h = 60;
  const step = w / (values.length - 1);
  const points = values.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${HUB_NAVY}0A` }}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: HUB_NAVY, opacity: 0.55 }}>Value trend</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={HUB_NAVY}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    </div>
  );
};

// ─── Equity unlocked widget ────────────────────────────────────────
const EquityCard = ({ equity, token, refresh }) => {
  const shownEquity = useCountUp(equity.amount || 0, 1200);
  const [busy, setBusy] = useState(false);
  const book = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/hub/public/${token}/book-equity-review`);
      toast.success('Equity review requested — your broker will reach out shortly');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to book review');
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="hub-equity-card"
      className="rounded-3xl p-6 sm:p-7 relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, ${HUB_NAVY} 0%, ${HUB_NAVY_LIGHT} 100%)`,
        color: HUB_CREAM,
      }}
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, ${HUB_CREAM} 1px, transparent 0)`,
        backgroundSize: '22px 22px',
      }} />
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: HUB_CREAM, opacity: 0.65 }}>Equity you&apos;ve unlocked</p>
            <p
              className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter"
              style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }}
              data-testid="hub-equity-amount"
            >
              {fmtCurrency(Math.round(shownEquity))}
            </p>
            <p className="mt-3 text-sm" style={{ color: HUB_CREAM, opacity: 0.75 }}>
              That&apos;s {equity.equity_pct}% of your property — money that can work for you now.
            </p>
          </div>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${HUB_CREAM}18` }}>
            <Gem className="w-6 h-6" style={{ color: HUB_CREAM }} />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-3" style={{ backgroundColor: `${HUB_CREAM}0F`, border: `1px solid ${HUB_CREAM}25` }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_CREAM, opacity: 0.6 }}>Property value</p>
            <p className="text-sm font-medium mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }}>{fmtCurrency(equity.property_value)}</p>
          </div>
          <div className="rounded-2xl p-3" style={{ backgroundColor: `${HUB_CREAM}0F`, border: `1px solid ${HUB_CREAM}25` }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: HUB_CREAM, opacity: 0.6 }}>Loan balance</p>
            <p className="text-sm font-medium mt-1" style={{ color: HUB_CREAM, fontFamily: HUB_FONT_HEAD }}>{fmtCurrency(equity.loan_balance)}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={book}
          disabled={busy}
          data-testid="hub-book-equity-review"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: HUB_CREAM, color: HUB_NAVY }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Book an equity review
        </button>
      </div>
    </div>
  );
};

// ─── Rate watch alert ──────────────────────────────────────────────
const RateWatchCard = ({ rate, token, refresh }) => {
  const [busy, setBusy] = useState(false);
  const savings = rate.monthly_savings_potential;
  const alertActive = rate.alert_active;
  const book = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/hub/public/${token}/book-rate-review`);
      toast.success('Rate review requested — your broker will crunch the numbers today');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to book review');
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="hub-rate-watch-card"
      className="rounded-3xl p-6 sm:p-7 relative overflow-hidden"
      style={{
        backgroundColor: alertActive ? '#FEF3C7' : '#fff',
        border: `1px solid ${alertActive ? '#F59E0B' : `${HUB_NAVY}12`}`,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em]"
             style={{ color: alertActive ? '#92400E' : HUB_NAVY, opacity: alertActive ? 1 : 0.55 }}>
            {alertActive ? 'Rate alert' : 'Rate watch'}
          </p>
          <div className="mt-2 flex items-baseline gap-3">
            <p
              className="text-4xl sm:text-5xl font-light tracking-tighter"
              style={{ color: HUB_NAVY, fontFamily: HUB_FONT_HEAD, letterSpacing: '-0.03em' }}
              data-testid="hub-rate-current"
            >
              {rate.current_rate}%
            </p>
            <div className="flex flex-col">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: HUB_NAVY, opacity: 0.55 }}>Market best</p>
              <p className="text-lg font-medium" style={{ color: alertActive ? '#B45309' : HUB_NAVY, fontFamily: HUB_FONT_HEAD }}
                 data-testid="hub-rate-market-best">
                {rate.market_best_rate}%
              </p>
            </div>
          </div>

          {alertActive && savings != null && savings > 0 && (
            <p className="mt-3 text-sm font-medium" style={{ color: '#92400E' }}>
              Switching could save you around <strong>{fmtCurrency(savings)}/month</strong>{rate.market_best_lender ? ` (via ${rate.market_best_lender})` : ''}.
            </p>
          )}
          {!alertActive && (
            <p className="mt-3 text-sm" style={{ color: HUB_NAVY, opacity: 0.7 }}>
              Your rate with {rate.current_lender || 'your lender'} is competitive — we&apos;ll keep watching and let you know if that changes.
            </p>
          )}
          <p className="text-[11px] mt-3 flex items-center gap-1.5" style={{ color: HUB_NAVY, opacity: 0.5 }}>
            <RefreshCw className="w-3 h-3" /> Last checked {fmtDate(rate.last_checked)}
          </p>
        </div>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: alertActive ? '#F59E0B' : `${HUB_NAVY}0A` }}>
          <Percent className="w-6 h-6" style={{ color: alertActive ? HUB_CREAM : HUB_NAVY }} />
        </div>
      </div>

      {alertActive && (
        <button
          type="button"
          onClick={book}
          disabled={busy}
          data-testid="hub-book-rate-review"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold py-3 transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: HUB_NAVY, color: HUB_CREAM }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Book a rate review
        </button>
      )}
    </div>
  );
};
